import { useEffect, useState, useRef, ReactElement } from "react";
import { Account, AccountDetailValue } from "../types";
import { formatCurrency as formatCurrencyUtil, formatAmountOnly as formatAmountOnlyUtil } from "../utils/transactionUtils";
import { useAccountHandlers } from "./Accounts/hooks/useAccountHandlers";
import { getAccountDetailsForDisplay as getAccountDetailsForDisplayUtil } from "../utils/accountHelpers";

import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Skeleton,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountSummaryCards from "./Accounts/components/AccountSummaryCards";
import ActiveAccountsSection from "./Accounts/components/ActiveAccountsSection";
import ArchivedAccountsSection from "./Accounts/components/ArchivedAccountsSection";
import AccountMenu from "./Accounts/components/AccountMenu";
import ArchiveConfirmationDialog from "./Accounts/components/ArchiveConfirmationDialog";
import UnarchiveConfirmationDialog from "./Accounts/components/UnarchiveConfirmationDialog";
import TransferDialog, { TransferFormData as TransferDialogFormData } from "../components/transfers/TransferDialog";
import { useAccountGroups } from "./Accounts/hooks/useAccountGroups";
import { useAccounts } from "./Accounts/hooks/useAccounts";
import { useCurrencies } from "./Accounts/hooks/useCurrencies";
import { useAccountTransactionsCheck } from "./Accounts/hooks/useAccountTransactionsCheck";
import { useAccountComputedValues } from "./Accounts/hooks/useAccountComputedValues";
import { useAccountUIState } from "./Accounts/hooks/useAccountUIState";
import { useTransferState } from "./Accounts/hooks/useTransferState";
import { useAccountDragAndDrop } from "./Accounts/hooks/useAccountDragAndDrop";
import { useTransferSubmission } from "./Accounts/hooks/useTransferSubmission";
import { useAccountForm } from "./Accounts/hooks/useAccountForm";
import { useAccountArchive } from "./Accounts/hooks/useAccountArchive";
import AccountDialog from "./Accounts/components/AccountDialog";
import { copyToClipboard } from "../utils/transactionUtils";

interface AccountsProps {
  workspaceId: string;
  enableOrdering?: boolean; // Enable drag-and-drop ordering UI
  hideTransferButton?: boolean; // Hide the transfer button (for settings page)
}

interface FormData {
  accountName: string;
  group: string;
  amount: string;
  currency: string;
  description: string;
  details?: AccountDetailValue;
}

interface TransferData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  transferRate: string; // Exchange rate for currency conversion
  date: string; // Date in YYYY-MM-DD format
  time?: string; // Optional time field (for compatibility with TransferDialog)
  note: string; // Optional note
  attachmentFile: File | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  attachmentType: string | null;
}

interface Summary {
  assets: number;
  liabilities: number;
  total: number;
}

interface GroupedAccounts {
  [key: string]: Account[];
}

function Accounts({ workspaceId, enableOrdering = false, hideTransferButton = false }: AccountsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { accounts, loading, setAccounts } = useAccounts(workspaceId);
  const accountGroups = useAccountGroups(workspaceId);
  const {
    currencies,
    defaultCurrency,
    valuesVisibilityMode,
    valuesVisible,
    setValuesVisible,
  } = useCurrencies(workspaceId);
  
  // Account transactions check
  const accountHasTransactions = useAccountTransactionsCheck(workspaceId, accounts);
  
  // UI state management
  const {
    expandedAccountDetails,
    setExpandedAccountDetails,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarElement,
    setSnackbarElement,
    toggleAccountDetails,
    toggleValuesVisibility,
  } = useAccountUIState({
    workspaceId,
    valuesVisibilityMode,
    valuesVisible,
    setValuesVisible,
  });

  // Archive/unarchive logic - must be before accountHandlers to use selectedAccount
  const {
    archiveConfirmOpen,
    setArchiveConfirmOpen,
    accountToArchive,
    setAccountToArchive,
    unarchiveConfirmOpen,
    setUnarchiveConfirmOpen,
    accountToUnarchive,
    setAccountToUnarchive,
    handleArchiveClick: handleArchiveClickFromHook,
    handleArchiveConfirm,
    handleUnarchiveClick: handleUnarchiveClickFromHook,
    handleUnarchiveConfirm,
  } = useAccountArchive({
    workspaceId,
    setSnackbarElement,
    setSnackbarOpen,
  });

  // Get default currency's decimal places for display
  const defaultCurrencyData = currencies.find(c => c.code === defaultCurrency);
  const defaultDecimalPlaces = defaultCurrencyData?.decimalPlaces;
  
  // Create formatCurrency wrapper that uses the default currency's decimal places
  const formatCurrency = (amount: number, currency: string = defaultCurrency): string => {
    // If currency matches default currency, use its decimal places
    if (currency === defaultCurrency && defaultDecimalPlaces !== undefined) {
      return formatCurrencyUtil(amount, currency, defaultDecimalPlaces);
    }
    // Otherwise, find the currency's decimal places
    const currencyData = currencies.find(c => c.code === currency);
    const currencyDecimalPlaces = currencyData?.decimalPlaces;
    return formatCurrencyUtil(amount, currency, currencyDecimalPlaces);
  };

  // Amount only (no currency code) for mobile Settings summary cards
  const formatAmountOnly = (amount: number, _currency?: string): string =>
    formatAmountOnlyUtil(amount, defaultDecimalPlaces ?? 2);

  // Account handlers hook - manages all account CRUD operations
  const accountHandlers = useAccountHandlers({
    workspaceId,
    accounts,
    accountHasTransactions,
    formatCurrency: formatCurrency,
    setSnackbarElement,
    setSnackbarOpen,
  });
  
  // Extract state and handlers from hook
  const {
    formData,
    setFormData,
    editingAccount,
    setEditingAccount,
    initialFormData,
    setInitialFormData,
    showAccountDetails,
    setShowAccountDetails,
    initialShowAccountDetails,
    setInitialShowAccountDetails,
    accountNameError,
    setAccountNameError,
    isSavingAccount,
    setIsSavingAccount,
    creatingAccountName,
    setCreatingAccountName,
    createDialogOpen,
    setCreateDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    menuAnchor,
    setMenuAnchor,
    selectedAccount,
    setSelectedAccount,
    handleCreateClick,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handleDuplicate,
    handleDeleteClick,
    handleDeleteConfirm,
    handleSubmit,
    handleDialogClose,
  } = accountHandlers;
  
  // Wrapper handlers that integrate with menu system
  const handleArchiveClick = (): void => {
    if (!selectedAccount) return;
    handleMenuClose();
    handleArchiveClickFromHook(selectedAccount);
  };
  
  const handleUnarchiveClick = (): void => {
    if (!selectedAccount) return;
    handleMenuClose();
    handleUnarchiveClickFromHook(selectedAccount);
  };

  // Update formData currency when default currency changes
  useEffect(() => {
    if (!editingAccount && formData.currency && !currencies.find(c => c.code === formData.currency && c.rate !== null) && formData.currency !== defaultCurrency) {
      setFormData(prev => ({ ...prev, currency: defaultCurrency }));
    }
  }, [defaultCurrency, currencies, editingAccount, formData.currency, setFormData]);

  // Transfer state management
  const {
    transferDialogOpen,
    setTransferDialogOpen,
    transferData,
    setTransferData,
    uploadingAttachment,
    setUploadingAttachment,
    transferFileInputRef,
    handleTransferClick,
    handleTransferDialogClose,
    handleTransferFileChange,
    handleRemoveTransferAttachment,
    getConvertedAmountLocal,
    isTransferFormValid,
  } = useTransferState({
    accounts,
    currencies,
    defaultCurrency,
  });

  // Fee field visibility state
  const [showTransferFee, setShowTransferFee] = useState<boolean>(false);

  // Currency rate helpers are now imported from accountHelpers

  // Note: Accounts are now fetched via useAccounts hook

  // Computed values (active/archived accounts, summary, grouped accounts)
  const {
    activeAccounts,
    archivedAccounts,
    summary,
    groupedActiveAccounts,
    groupedArchivedAccounts,
    getGroupBalance,
    getArchivedGroupBalance,
  } = useAccountComputedValues({
    accounts,
    accountGroups,
    defaultCurrency,
    currencies,
  });

  // Drag and drop for account ordering
  const { sensors, handleAccountDragEnd } = useAccountDragAndDrop({
    workspaceId,
    accounts,
    setAccounts,
    groupedActiveAccounts,
    setSnackbarElement,
    setSnackbarOpen,
  });

  // Note: Account CRUD handlers are now managed by useAccountHandlers hook

  // Note: Transfer state and handlers are now managed by useTransferState hook
  // Note: Transfer submission logic is now managed by useTransferSubmission hook

  // Transfer submission handler
  const { handleTransferSubmit } = useTransferSubmission({
    workspaceId,
    accounts,
    transferData,
    setTransferData,
    setTransferDialogOpen,
    setUploadingAttachment,
    setSnackbarElement,
    setSnackbarOpen,
    transferFileInputRef,
    defaultCurrency,
  });

  // Note: handleDialogClose is now managed by useAccountHandlers hook
  // Note: Account form logic is now managed by useAccountForm hook
  // Note: Archive/unarchive logic is now managed by useAccountArchive hook

  // Account form logic
  const {
    getSelectedGroupDetails,
    handleDetailValueChange,
    hasFormChanged,
    isAccountFormValid,
  } = useAccountForm({
    formData,
    setFormData,
    editingAccount,
    initialFormData,
    showAccountDetails,
    initialShowAccountDetails,
    accountNameError,
    accountGroups,
  });


  // Note: Transfer form validation is now managed by useTransferState hook

  // Copy to clipboard (account details) – show "Copied!" in tooltip near the icon
  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyToClipboard = async (text: string, feedbackKey?: string): Promise<void> => {
    const ok = await copyToClipboard(text);
    if (ok && feedbackKey) {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
      setLastCopiedKey(feedbackKey);
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setLastCopiedKey(null);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
    }
  };

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  // Note: UI state (expanded details, snackbar, visibility toggle) is now managed by useAccountUIState hook

  // Get account details for display (using utility function)
  const getAccountDetailsForDisplay = (account: Account): Array<{ name: string; value: string | number }> => {
    return getAccountDetailsForDisplayUtil(account, accountGroups);
  };

  // Note: Transfer dialog close, converted amount calculation, and form validation are now managed by useTransferState hook

  // formatRate is now imported from accountHelpers

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      {/* Main Content - responsive for mobile and desktop */}
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
      >
        {/* Top Bar - Summary cards */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            p: { xs: 1, sm: 1, md: 1.25 },
            pt: { xs: 0.75, sm: 0.75, md: 0.75 },
            marginBottom: 1.25,
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          {/* Summary Cards */}
          <AccountSummaryCards
            summary={summary}
            defaultCurrency={defaultCurrency}
            valuesVisible={valuesVisible}
            formatCurrency={formatCurrency}
            formatAmountOnly={isMobile && hideTransferButton ? formatAmountOnly : undefined}
            onToggleVisibility={toggleValuesVisibility}
          />
        </Paper>

        {/* Accounts List */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 0 }}>
          {loading ? (
            <Stack spacing={1}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={56} />
              ))}
            </Stack>
          ) : activeAccounts.length === 0 && archivedAccounts.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: 0.5,
                textAlign: "center",
              }}
            >
              <Stack spacing={1} alignItems="center">
                <AccountBalanceIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary">
                  No accounts yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the button above to create your first account
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <Stack spacing={0}>
              {/* Active Accounts */}
              <ActiveAccountsSection
                accountGroups={accountGroups}
                groupedActiveAccounts={groupedActiveAccounts}
                getGroupBalance={getGroupBalance}
                getAccountDetailsForDisplay={getAccountDetailsForDisplay}
                expandedAccountDetails={expandedAccountDetails}
                onToggleDetails={toggleAccountDetails}
                onCopyToClipboard={handleCopyToClipboard}
                lastCopiedKey={lastCopiedKey}
                isMobile={isMobile}
                onMenuOpen={handleMenuOpen}
                formatCurrency={formatCurrency}
                valuesVisible={valuesVisible}
                defaultCurrency={defaultCurrency}
                enableOrdering={enableOrdering}
                sensors={sensors}
                onAccountDragEnd={handleAccountDragEnd}
              />

              {/* Archived Accounts Section - Always visible, collapsed by default */}
              <ArchivedAccountsSection
                archivedAccounts={archivedAccounts}
                accountGroups={accountGroups}
                groupedArchivedAccounts={groupedArchivedAccounts}
                getArchivedGroupBalance={getArchivedGroupBalance}
                getAccountDetailsForDisplay={getAccountDetailsForDisplay}
                expandedAccountDetails={expandedAccountDetails}
                onToggleDetails={toggleAccountDetails}
                onCopyToClipboard={handleCopyToClipboard}
                lastCopiedKey={lastCopiedKey}
                isMobile={isMobile}
                onMenuOpen={handleMenuOpen}
                formatCurrency={formatCurrency}
                valuesVisible={valuesVisible}
                defaultCurrency={defaultCurrency}
              />
            </Stack>
          )}
        </Box>

        {/* Fixed Bottom Section - action buttons */}
        <Paper
          elevation={0}
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            p: { xs: 1.5, sm: 2 },
            pt: { xs: 1.25, sm: 2 },
            pb: { xs: "calc(env(safe-area-inset-bottom, 0px) + 12px)", sm: 2 },
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              sx={{
                py: { xs: 1.25, sm: 1.5 },
                fontSize: { xs: "0.9375rem", sm: "1rem" },
              }}
            >
              Create Account
            </Button>
            {!hideTransferButton && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                onClick={handleTransferClick}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  fontSize: { xs: "0.9375rem", sm: "1rem" },
                }}
              >
                Transfer
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>

      {/* Account Menu */}
      <AccountMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        selectedAccount={selectedAccount}
        accountHasTransactions={accountHasTransactions}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onArchive={handleArchiveClick}
        onUnarchive={handleUnarchiveClick}
        onDelete={handleDeleteClick}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSelectedAccount(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={selectedAccount?.accountName || ""}
        itemType="account"
        requireNameConfirmation={true}
      />

      {/* Archive Confirmation Dialog */}
      <ArchiveConfirmationDialog
        open={archiveConfirmOpen}
        onClose={() => {
          setArchiveConfirmOpen(false);
          setSelectedAccount(null);
        }}
        onConfirm={handleArchiveConfirm}
        account={selectedAccount}
      />

      {/* Unarchive Confirmation Dialog */}
      <UnarchiveConfirmationDialog
        open={unarchiveConfirmOpen}
        onClose={() => {
          setUnarchiveConfirmOpen(false);
          setAccountToUnarchive(null);
        }}
        onConfirm={handleUnarchiveConfirm}
        account={accountToUnarchive}
      />

      {/* Create/Edit Account Dialog */}
      <AccountDialog
        open={createDialogOpen || editDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        editingAccount={editingAccount}
        formData={formData}
        setFormData={setFormData}
        showAccountDetails={showAccountDetails}
        setShowAccountDetails={setShowAccountDetails}
        initialShowAccountDetails={initialShowAccountDetails}
        setInitialShowAccountDetails={setInitialShowAccountDetails}
        accountGroups={accountGroups}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        accountNameError={accountNameError}
        setAccountNameError={setAccountNameError}
        isSavingAccount={isSavingAccount}
        creatingAccountName={creatingAccountName}
        setCreatingAccountName={setCreatingAccountName}
        getSelectedGroupDetails={getSelectedGroupDetails}
        handleDetailValueChange={handleDetailValueChange}
        hasFormChanged={hasFormChanged}
        isAccountFormValid={isAccountFormValid}
      />

      {/* Transfer Dialog */}
      <TransferDialog
        open={transferDialogOpen}
        onClose={handleTransferDialogClose}
        onSubmit={handleTransferSubmit}
        formData={transferData as TransferDialogFormData}
        setFormData={setTransferData as React.Dispatch<React.SetStateAction<TransferDialogFormData>>}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        showTime={false}
        showBalanceHelpers={true}
        showRefreshRate={false}
        editingTransaction={undefined}
        isSubmitting={false}
        uploadingAttachment={uploadingAttachment}
        onFileChange={handleTransferFileChange}
        onRemoveAttachment={handleRemoveTransferAttachment}
        fileInputRef={transferFileInputRef}
        isValid={!!isTransferFormValid}
        getConvertedAmount={getConvertedAmountLocal}
        setShowFee={setShowTransferFee}
      />

      {/* Snackbar for demo mode alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }} severity="info" sx={{ width: "100%" }}>
          {snackbarElement || snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Accounts;
